import { useState, type ReactNode } from "react";
import { DefinitionRows, GridHead, GridRow, ds, type GridColumn } from "@/shared/components/ds";
import { humanize, show } from "../format";
import styles from "./launchDetail.module.css";

/*
    The small pieces the launch tabs are built from: labelled rows of scalars, a mini block
    for an expanded row, and a grid whose rows open to show what does not fit in a column.
*/

const isEmpty = (v: unknown) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

export type FieldSpec = [label: string, value: unknown, opts?: { mono?: boolean; full?: boolean }];

/** Label | value rows. A missing value reads "—" in muted ink. */
export function Fields({ fields }: { fields: FieldSpec[] }) {
    return (
        <DefinitionRows rows={fields.map(([label, value, opts]) => ({
            label, value: show(value), empty: isEmpty(value), mono: opts?.mono, full: opts?.full,
        }))} />
    );
}

/** `record` picked by `keys`, labelled; `keys` as [key, label] overrides the humanized label. */
export function pick<T extends object>(record: T, keys: (keyof T | [keyof T, string] | [keyof T, string, { mono?: boolean; full?: boolean }])[]): FieldSpec[] {
    return keys.map((k) => {
        const [key, label, opts] = Array.isArray(k) ? k : [k, humanize(String(k)), undefined];
        return [label, (record as Record<string, unknown>)[String(key)], opts];
    });
}

/** The uppercase-label mini block of an expanded row. */
export function Mini({ label, value, mono = false }: { label: string; value: unknown; mono?: boolean }) {
    const empty = isEmpty(value);
    return (
        <div style={{ minWidth: 0 }}>
            <div className={styles.miniLabel}>{label}</div>
            <div className={`${styles.miniValue} ${empty ? ds.muted : ""} ${mono && !empty ? ds.mono : ""}`}>{show(value)}</div>
        </div>
    );
}

export function YesNo({ value }: { value?: boolean | null }) {
    if (value === null || value === undefined) return <span className={ds.muted}>—</span>;
    return <span className={value ? undefined : styles.zero}>{value ? "Yes" : "No"}</span>;
}

export interface ExpandableRowSpec {
    key: string | number;
    cells: ReactNode[];
    /** What opens beneath the row; a row without one is plain. */
    detail?: ReactNode;
}

const CHEVRON: GridColumn = { label: "", track: "36px" };

/**
 * A grid on the table tokens whose rows open to a slate panel. The chevron column is added
 * here; `columns` describe only the data. The panel is indented to the second column unless
 * `flush`.
 */
export function ExpandableGrid({ columns, rows, minWidth, flush = false, empty }: {
    columns: GridColumn[]; rows: ExpandableRowSpec[]; minWidth?: number | string; flush?: boolean; empty?: ReactNode;
}) {
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const all = [...columns, CHEVRON];
    return (
        <div className={styles.scroll} role="table">
            <div style={{ minWidth }}>
                <GridHead columns={all} />
                {rows.map((r) => {
                    const isOpen = !!open[r.key];
                    const expandable = r.detail !== undefined && r.detail !== null;
                    return (
                        <div key={r.key} className={styles.rowWrap}>
                            <GridRow columns={all} className={isOpen ? styles.open : undefined}
                                onClick={expandable ? () => setOpen({ ...open, [r.key]: !isOpen }) : undefined}
                                cells={[...r.cells, expandable
                                    ? <span className={styles.chev} aria-label={isOpen ? "Collapse" : "Expand"}><i className={`bi bi-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true" /></span>
                                    : null]} />
                            {isOpen && <div className={`${styles.panel} ${flush ? styles.panelFlush : ""}`}>{r.detail}</div>}
                        </div>
                    );
                })}
                {rows.length === 0 && empty && <div className={styles.noMatch}>{empty}</div>}
            </div>
        </div>
    );
}
